import { useState } from "react";
import { reportsApi, getOrganizationId } from "./reportsApi";

export const useReportGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const clearError = () => setError(null);

  const generateReport = async ({
    reportType,
    selectedCategories,
    dateOptions,
    vehicles,
    routes,
    drivers,
    stops,
    users, // Added users for inventory reports
    siteId,
  }) => {
    setLoading(true);
    setError(null);

    try {
      const orgId = getOrganizationId();
      if (!orgId) {
        throw new Error("Organization ID is required. Please log in again.");
      }
      if (!dateOptions.duration) {
        throw new Error("Date option is required.");
      }
      if (
        dateOptions.duration === "custom" &&
        (!dateOptions.customDateRange.startDate ||
          !dateOptions.customDateRange.endDate)
      ) {
        throw new Error("Custom date range requires start and end dates.");
      }

      // Check if any category is selected
      if (!selectedCategories || Object.keys(selectedCategories).length === 0) {
        throw new Error(
          "Please select at least one category (Vehicles, Routes, Drivers, Stops, or Users)."
        );
      }

      // Get the first (and only) selected category
      const categoryKey = Object.keys(selectedCategories)[0];
      const { isAllSelected, selectedItems } = selectedCategories[categoryKey];

      let category, selectedIds;

      // Map category keys to API format and get selected IDs
      if (categoryKey === "Vehicles") {
        category = "vehicles";
        if (isAllSelected) {
          selectedIds = vehicles.map((v) => v.device_id);
        } else {
          selectedIds = selectedItems
            .map(
              (name) =>
                vehicles.find((v) => (v.VehicleNumber || v.device_id) === name)
                  ?.device_id
            )
            .filter((id) => id);
        }
      } else if (categoryKey === "Routes") {
        category = "routes";
        if (isAllSelected) {
          selectedIds = routes.map((r) => r.id);
        } else {
          selectedIds = selectedItems
            .map(
              (name) =>
                routes.find((r) => (r.name || r.route_name) === name)?.id
            )
            .filter((id) => id);
        }
      } else if (categoryKey === "Drivers") {
        category = "drivers";
        if (isAllSelected) {
          selectedIds = drivers.map((d) => d.id);
        } else {
          selectedIds = selectedItems
            .map(
              (name) =>
                drivers.find(
                  (d) => `${d.first_name} ${d.last_name}`.trim() === name
                )?.id
            )
            .filter((id) => id);
        }
      } else if (categoryKey === "Stops") {
        category = "stops";
        if (isAllSelected) {
          selectedIds = stops.map((s) => s.id);
        } else {
          selectedIds = selectedItems
            .map((name) => stops.find((s) => s.name === name)?.id)
            .filter((id) => id);
        }
      } else if (categoryKey.toLowerCase() === "users") {
        category = "users";
        if (isAllSelected) {
          selectedIds = users.map((u) => u.id);
        } else {
          selectedIds = selectedItems
            .map(
              (name) =>
                users.find(
                  (u) => (u.name || u.userName || u.email || "") === name
                )?.id
            )
            .filter((id) => id);
        }
      } else {
        throw new Error(`Invalid category: ${categoryKey}`);
      }

      // Prepare parameters for the new API structure
      const params = {
        orgId,
        siteId: siteId || null,
        category,
        selectedIds,
        selectAll: isAllSelected,
        dateOption: dateOptions.duration,
        customStartDate: dateOptions.customDateRange.startDate,
        customEndDate: dateOptions.customDateRange.endDate,
      };

      console.log("Report Parameters:", params); // Debug log

      let data;

      // Call the appropriate report function
      if (reportType === "overspeed") {
        data = await reportsApi.getOverspeedReport(params);
      } else if (reportType === "activity") {
        data = await reportsApi.getActivityReport(params);
      } else if (reportType === "sos") {
        data = await reportsApi.getSosReport(params);
      } else if (reportType === "inventory") {
        data = await reportsApi.getInventoryReport(params);
      } else if (reportType === "runtime") {
        data = await reportsApi.getRuntimeReport(params);
      } else if (reportType === "trip") {
        data = await reportsApi.getTripReport(params);
      } else if (reportType === "deviation") {
        data = await reportsApi.getRouteDeviationReport(params);
      } else if (reportType === "halt") {
        data = await reportsApi.getHaltReport(params);
      }else if (reportType === "entryExit") {
        data = await reportsApi.getEntryExitReport(params);
      }
       else {
        throw new Error("Invalid report type");
      }

      // Deduplicate data by deviceId if needed
      // if (data?.data && Array.isArray(data.data)) {
      //   const uniqueData = [...new Map(data.data.map(item => [item.deviceId, item])).values()];
      //   data = { ...data, data: uniqueData, count: uniqueData.length };
      // }

      console.log("Report Data:", data); // Debug log
      setReportData(data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to generate report";
      console.error("Report Error:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { generateReport, loading, error, reportData, clearError };
};
